import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import CookieConsent from '@/components/CookieConsent';
import AnalyticsManager from '@/components/AnalyticsManager';
import { QuoteModalProvider } from '@/context/QuoteModalContext';

// Layout
import Header from '@/components/Layout/Header';
import Footer from '@/components/Layout/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import SEO from '@/components/SEO';

// Pages — code-split via React.lazy (each page loads only when first visited)
const Homepage = lazy(() => import('@/pages/Homepage'));
const ServicePage = lazy(() => import('@/pages/ServicePage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const ClaimsPage = lazy(() => import('@/pages/ClaimsPage'));
const SimplePage = lazy(() => import('@/pages/SimplePage'));
const PartnersPage = lazy(() => import('@/pages/PartnersPage'));
const PartnerDetailPage = lazy(() => import('@/pages/PartnerDetailPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUsePage'));
const CareersPage = lazy(() => import('@/pages/CareersPage'));
const GonePage = lazy(() => import('@/pages/GonePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// POC pages — development only
const POCAdminPage = lazy(() => import('@/pages/POCAdminPage'));
const POCContactTest = lazy(() => import('@/pages/POCContactTest'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

// MainLayout component moved outside of App to prevent re-creation on each render
const MainLayout = ({ children }) => (
  <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-cta focus:font-semibold focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-cta"
    >
      Skip to main content
    </a>
    <Header />
    <main id="main-content">{children}</main>
    <Footer />
  </>
);

const VALID_HUBS = ['auto-insurance', 'home-insurance', 'business-insurance', 'travel-insurance', 'life-insurance', 'group-benefits', 'marine-insurance'];

function ValidatedServiceRoute() {
  const { hub } = useParams();
  if (!VALID_HUBS.includes(hub)) {
    return <MainLayout><NotFoundPage /></MainLayout>;
  }
  return <MainLayout><ServicePage /></MainLayout>;
}

// /make-a-payment: no real page exists in this codebase. Until Emergent can serve
// a true server-side redirect (see redirects.json / SITE_RULES.md), we do a
// client-side redirect to /contact-us so visitors are never stranded on the 404.
// Kept noindex; the canonical points at /contact-us.
function MakePaymentRedirect() {
  return (
    <>
      <SEO title="Redirecting" description="Redirecting to contact page." noindex={true} canonicalPath="/contact-us" />
      <Navigate to="/contact-us" replace />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QuoteModalProvider>
        <div className="App">
          <Toaster />
          <CookieConsent />
          <AnalyticsManager />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
            {/* Homepage */}
            <Route path="/" element={<MainLayout><Homepage /></MainLayout>} />

            {/* All Services */}
            <Route path="/services" element={<MainLayout><ServicesPage /></MainLayout>} />

            {/* 410 Gone - Permanently Removed Pages */}
            <Route path="/car-insurance-paula-zhang" element={<MainLayout><GonePage /></MainLayout>} />
            <Route path="/car-insurance-bcla" element={<MainLayout><GonePage /></MainLayout>} />

            {/* Service Hubs & Sub-pages — dynamic routes validated against the 7 known hub slugs */}
            <Route path="/:hub" element={<ValidatedServiceRoute />} />
            <Route path="/:hub/:service" element={<ValidatedServiceRoute />} />

            {/* Contact & Forms */}
            <Route path="/contact-us" element={<MainLayout><ContactPage /></MainLayout>} />

            {/* Blog (English) */}
            <Route path="/blog" element={<MainLayout><BlogPage lang="en" /></MainLayout>} />
            <Route path="/blog/:slug" element={<MainLayout><BlogPostPage lang="en" /></MainLayout>} />

            {/* Blog (Portuguese) — /pt/ prefix for all Portuguese pages */}
            <Route path="/pt/blog" element={<MainLayout><BlogPage lang="pt-BR" /></MainLayout>} />
            <Route path="/pt/blog/:slug" element={<MainLayout><BlogPostPage lang="pt-BR" /></MainLayout>} />

            {/* About & Company */}
            <Route path="/about-us" element={<MainLayout><AboutPage /></MainLayout>} />
            <Route path="/meet-the-team" element={<MainLayout><TeamPage /></MainLayout>} />
            
            {/* Partners */}
            <Route path="/partners" element={<MainLayout><PartnersPage /></MainLayout>} />
            <Route path="/partners/:slug" element={<MainLayout><PartnerDetailPage /></MainLayout>} />
            
            {/* Legal */}
            <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
            <Route path="/terms-of-use" element={<MainLayout><TermsOfUsePage /></MainLayout>} />

            {/* Support */}
            <Route path="/claims-support" element={<MainLayout><ClaimsPage /></MainLayout>} />
            <Route
              path="/locations"
              element={
                <MainLayout>
                  <SimplePage
                    title="Insurance Office Burnaby | 6696 Hastings Street"
                    description="Visit our Burnaby office at 6696 Hastings St for ICBC Autoplan, home, and business insurance. Serving Burnaby, Vancouver, and the Lower Mainland since 1985."
                    content="We're conveniently located on Hastings Street in Burnaby. Stop by during business hours or call ahead to book an appointment with one of our advisors."
                  />
                </MainLayout>
              }
            />
            {/* Careers */}
            <Route path="/careers" element={<MainLayout><CareersPage /></MainLayout>} />

            {/* Admin routes intentionally removed — admin login/dashboard disabled */}
            <Route path="/admin/login" element={<MainLayout><NotFoundPage /></MainLayout>} />
            <Route path="/admin/dashboard" element={<MainLayout><NotFoundPage /></MainLayout>} />
            <Route path="/admin/*" element={<MainLayout><NotFoundPage /></MainLayout>} />

            {/* POC Routes (development only) */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <Route path="/poc-admin" element={<POCAdminPage />} />
                <Route path="/poc-contact" element={<POCContactTest />} />
              </>
            )}

            {/* SEO-Friendly Redirects */}
            <Route path="/make-a-payment" element={<MakePaymentRedirect />} />
            <Route path="/life-insurance/criticalillness" element={<Navigate to="/life-insurance/critical-illness" replace />} />
            <Route path="/life-insurance/di" element={<Navigate to="/life-insurance/disability" replace />} />
            <Route path="/business/contractors" element={<Navigate to="/business-insurance/general-contractors" replace />} />

            {/* 404 */}
            <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />
          </Routes>
            </Suspense>
        </BrowserRouter>
      </div>
      </QuoteModalProvider>
    </HelmetProvider>
  );
}

export default App;
